/**
 * Pure stage-move resolution for the Workspace pipeline board (PR4a).
 *
 * The drag-and-drop board calls this when a card is dropped into a stage
 * column to decide WHAT the UI does next — a direct move, or open a form
 * first. Kept pure (no React, no fetch, no drag types) so the gate/branch
 * logic is unit-tested independent of the @dnd-kit gesture, per the r1
 * condition. The SERVER still enforces the real gates (e.g. 409 gate_not_met
 * when Client Setup / Billing checklist items aren't done) — this only routes
 * the interaction.
 *
 *   → client_setup, no client yet  → open the Client Setup form (name + id)
 *   → client_setup, already linked  → plain move (client create is idempotent)
 *   → assigned                      → open the Assign form (CM + units)
 *   → any other stage               → plain move
 */

export const CLIENT_SETUP_STAGE_KEY = 'client_setup';
export const ASSIGNED_STAGE_KEY = 'assigned';
/** The Case Assignment stage ("Ready for Assignment", 0098) — where unit leads
 *  pick the owning CM. Same is_system-locked-stage-set stability argument as
 *  the constants above. Used by the card's assignment-stage extras (prospect
 *  folder link + always-visible Care manager row). */
export const READY_FOR_ASSIGNMENT_STAGE_KEY = 'ready_for_assignment';
/** Mirrors the server's PAPERWORK_SENT_KEY (api/_shared/repos/_workItemsShared.ts:330).
 *  The intake process's stage set is is_system-locked, so this literal is
 *  stable — same precedent as the two constants above. Used by the
 *  Conversion ledger's founder-definition rate (paperwork received ÷ sent). */
export const PAPERWORK_SENT_STAGE_KEY = 'paperwork_sent';

export type StageMoveAction =
  | { action: 'move'; toStage: string }
  | { action: 'open-client-setup'; toStage: string }
  | { action: 'open-assign'; toStage: string };

export interface StageMoveItemState {
  /** The work_item's current stage key (a no-op drop onto the same column). */
  currentStageKey: string | null;
  /** Whether the work_item already has a linked client (client_id set). */
  hasClient: boolean;
  /** PR3 fix: the dragged card's kind. The client_setup/assigned special
   *  routing is INTAKE-ONLY — a process_item card on a board whose stage
   *  happens to reuse one of those bare keys must take the plain move path
   *  (its board has no client-create or CM-assign semantics). Optional and
   *  defaulting to intake behavior so pre-PR3 call sites/tests are unchanged
   *  (the board always passes it). */
  kind?: 'intake' | 'task' | 'process_item';
}

/**
 * Decide the interaction for dropping a card into `toStageKey`. Returns null
 * for a no-op (dropped back into its current column).
 */
export function resolveStageMove(
  toStageKey: string,
  item: StageMoveItemState,
): StageMoveAction | null {
  if (item.currentStageKey === toStageKey) return null; // no-op: same column

  // PR3: only an INTAKE card routes into the intake-specific dialogs; every
  // other kind is a plain move regardless of the target stage's bare key.
  if (item.kind !== undefined && item.kind !== 'intake') {
    return { action: 'move', toStage: toStageKey };
  }

  if (toStageKey === CLIENT_SETUP_STAGE_KEY && !item.hasClient) {
    return { action: 'open-client-setup', toStage: toStageKey };
  }
  if (toStageKey === ASSIGNED_STAGE_KEY) {
    return { action: 'open-assign', toStage: toStageKey };
  }
  return { action: 'move', toStage: toStageKey };
}

/** An intake card's prospect name split for the Client Setup form's
 *  first and last boxes: the first word, then the rest. Shared by the
 *  board's drag and the drawer's Done · advance. */
export function splitProspectName(prospect: string | null): { first: string; last: string } {
  const parts = (prospect ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0]!, last: '' };
  return { first: parts[0]!, last: parts.slice(1).join(' ') };
}
